import { useState, useEffect, useCallback, useRef } from "react";

import { PocketBaseClient } from "../Api/PocketbaseClient.js";
import { RecordModel } from "pocketbase";

const searchSongs = async (query: string) => {
  if (!query || query.trim() === "") return [];
  const filter = `songName~"${query}" || artistName~"${query}"`;
  try {
    const result = await PocketBaseClient.collection("songs").getFullList({
      filter,
    });
    return result || [];
  } catch (err) {
    console.error("Error searching songs:", err);
    return [];
  }
};

const searchSongsRandom = async () => {
  const results = await PocketBaseClient.collection("songs").getList(1, 300, {
    sort: "@random",
  });
  return results.items || [];
};

export const useSongSearch = () => {
  const [search, setSearch] = useState("");
  const [songResults, setSongResults] = useState<RecordModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (search.trim().length === 0 && !searchPromiseRef.current) {
      setLoading(true);
      searchPromiseRef.current = searchSongsRandom().then((listResults) => {
        console.log("searchSongsRandom", listResults);
        setSongResults(listResults);
        setLoading(false);
        searchPromiseRef.current = null;
      });
    }
  }, [search]);

  const executeSearch = useCallback(async (searchValue: string) => {
    setLoading(true);
    if (!searchValue || searchValue.trim().length < 3) {
      setSongResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    searchSongs(searchValue)
      .then((results) => {
        setSongResults(results);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error searching songs:", err);
        setSongResults([]);
        setLoading(false);
        setError(err.message);
      });
  }, []);

  // Use ref to store debounce timeout
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback(
    (searchValue: string) => {
      setLoading(true);
      setSearch(searchValue);
      setError(null);

      // Clear previous timeout
      if (!!debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Clear results immediately if search is too short
      if (!searchValue || searchValue.trim().length < 2) {
        setSongResults([]);
        setLoading(false);
        return;
      }

      // Debounce the search
      debounceTimeoutRef.current = setTimeout(() => {
        executeSearch(searchValue);
      }, 300);
    },
    [executeSearch]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    search,
    setSearch: handleSearch,
    songResults,
    setSongResults,
    loading,
    error,
  };
};
